import {
  ColumnDirective,
  ColumnsDirective,
  ExcelExport,
  GridComponent,
  Inject,
  Resize,
  Search,
  Sort,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import axios from "axios";
import { getCookie } from "cookies-next";
import { useEffect, useRef, useState } from "react";
import {
  BiChevronLeft,
  BiChevronRight,
  BiMessageSquareDetail,
} from "react-icons/bi";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Header, PageLoading } from "../../../components";
import { API_ROOT } from "../../../config";
import "../../table.css";
import "./platformSignatureRecap.css";

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const LIMIT_OPTIONS = [10, 25, 50, 100];

const PlatformSignatureRecap = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [pageLoading, setPageLoading] = useState(true);
  const [recapData, setRecapData] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    total_pages: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(50); // default 50

  const today = new Date();
  const [filters, setFilters] = useState({
    from: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: formatDate(today),
  });

  const fetchData = async (page = currentPage, limit = currentLimit) => {
    setPageLoading(true);
    try {
      const response = await axios.get(
        `${API_ROOT}/admin/recap/platform-signatures`,
        {
          params: { from: filters.from, to: filters.to, page, limit },
          headers: { Authorization: getCookie("admin_auth") },
        },
      );

      const { data: list, pagination: pg } = response.data.data;

      setRecapData(
        list.map((item, index) => ({
          Uuid: item.uuid,
          No: (pg.page - 1) * pg.limit + index + 1,
          namaBadanUsaha: item.name_badan_usaha,
          nameAssiger: item.name_assiger,
          balance: item.balance,
          jumlahTtePaperless: item.jum_tte_paperless,
          jumlahTteMarkany: item.jum_tte_markany,
          totalTte: item.total_tte,
          // simpan filter untuk diteruskan ke detail
          _from: filters.from,
          _to: filters.to,
        })),
      );
      setPagination(pg);
    } catch (error) {
      if (error.response?.status === 401) return navigate("/login");
      toast.error("Failed to get data");
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, currentLimit);
  }, [currentPage, currentLimit]);

  const handleFilter = () => {
    setCurrentPage(1);
    fetchData(1, currentLimit);
  };

  const handleLimitChange = (e) => {
    setCurrentLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  // Action button di kolom paling KIRI
  const actionTemplate = (props) =>
    props.Uuid ? (
      <div className="e-actionParent">
        <Link
          to={`/recap/platform-signatures-recap/${props.Uuid}?from=${props._from}&to=${props._to}`}
          className="e-action"
        >
          <BiMessageSquareDetail className="e-actionIcon" />
          <div>Detail</div>
        </Link>
      </div>
    ) : null;

  const queryCellInfo = (args) => args.cell?.classList.add("e-cell");

  const dataBound = () => {
    if (gridRef.current) {
      gridRef.current.autoFitColumns([
        "No",
        "namaBadanUsaha",
        "nameAssiger",
        "balance",
        "jumlahTtePaperless",
        "jumlahTteMarkany",
        "totalTte",
      ]);
    }
  };

  const toolbarClick = (args) => {
    if (gridRef.current && args.item.id.includes("excelexport")) {
      gridRef.current.excelExport({
        fileName:
          "Paperless - Digitalisasi Untuk Negeri Integrated Platform.xlsx",
      });
    }
  };

  const { page, total_pages, total } = pagination;

  return pageLoading ? (
    <PageLoading />
  ) : (
    <div className="m-2 md:m-10 mt-24 px-2 py-10 md:p-10 bg-white rounded-3xl">
      <div className="flex justify-between items-end pb-4">
        <Header title="Platform Signature Recap" />
      </div>

      {/* Filter */}
      <div className="flex mb-4 gap-4 items-end">
        <div>
          <label className="block text-sm mb-1">Dari</label>
          <input
            type="date"
            value={filters.from}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, from: e.target.value }))
            }
            className="block border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Sampai</label>
          <input
            type="date"
            value={filters.to}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, to: e.target.value }))
            }
            className="block border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          className="text-white w-44 py-2 px-4 rounded-md text-sm bg-green-500 hover:bg-green-600"
          onClick={handleFilter}
        >
          Filter
        </button>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <div className="w-fit">
          <GridComponent
            dataSource={recapData}
            width="auto"
            allowSorting
            allowTextWrap={true}
            textWrapSettings={{ wrapMode: "Content" }}
            toolbar={["Search", "ExcelExport"]}
            queryCellInfo={queryCellInfo}
            allowExcelExport={true}
            toolbarClick={toolbarClick}
            dataBound={dataBound}
            ref={gridRef}
          >
            <ColumnsDirective>
              {/* Action di paling KIRI */}
              <ColumnDirective
                field="Uuid"
                headerText="Aksi"
                textAlign="Center"
                isPrimaryKey={true}
                width={80}
                template={actionTemplate}
              />
              <ColumnDirective
                field="No"
                headerText="No"
                textAlign="Center"
                width={50}
              />
              <ColumnDirective
                field="namaBadanUsaha"
                headerText="Badan Usaha"
                textAlign="Left"
                template={(props) => <div>{props.namaBadanUsaha || "-"}</div>}
              />
              <ColumnDirective
                field="nameAssiger"
                headerText="Assigner"
                textAlign="Left"
              />
              <ColumnDirective
                field="balance"
                headerText="Balance"
                textAlign="Left"
              />
              <ColumnDirective
                field="jumlahTtePaperless"
                headerText="Jumlah TTe Paperless"
                textAlign="Left"
              />
              <ColumnDirective
                field="jumlahTteMarkany"
                headerText="Jumlah TTe Markany"
                textAlign="Left"
              />
              <ColumnDirective
                field="totalTte"
                headerText="Total TTe"
                textAlign="Left"
              />
            </ColumnsDirective>
            <Inject services={[Search, Toolbar, Sort, Resize, ExcelExport]} />
          </GridComponent>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <span>Tampilkan</span>
          <select
            value={currentLimit}
            onChange={handleLimitChange}
            className="border border-gray-300 rounded px-2 py-1"
          >
            {LIMIT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <span>
            data per halaman · Total <strong>{total}</strong> data
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
          >
            <BiChevronLeft size={20} />
          </button>
          {Array.from({ length: total_pages }, (_, i) => i + 1)
            .filter(
              (p) => p === 1 || p === total_pages || Math.abs(p - page) <= 1,
            )
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((item, idx) =>
              item === "..." ? (
                <span key={`e-${idx}`} className="px-2">
                  …
                </span>
              ) : (
                <button
                  key={item}
                  onClick={() => setCurrentPage(item)}
                  className={`w-8 h-8 rounded text-sm ${
                    item === page
                      ? "bg-green-500 text-white"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {item}
                </button>
              ),
            )}
          <button
            onClick={() => setCurrentPage((p) => Math.min(total_pages, p + 1))}
            disabled={page >= total_pages}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
          >
            <BiChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatformSignatureRecap;
